/**
 * Château Frontenac — finesse pass.
 * Architecture reads from reveals, cornices, string courses, copper eaves —
 * not from fewer boxes + fog. Footprint/orientation preserved.
 */
import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
} from "three";
import { stageMat, withOutline, type StageMaterial } from "../style/toon";

const stone = stageMat(0xc4bbb0, { roughness: 0.88 });
const stoneMid = stageMat(0xb0a698, { roughness: 0.86 });
const stoneDark = stageMat(0x8f867c, { roughness: 0.9 });
const stoneLight = stageMat(0xd2c8bc, { roughness: 0.82 });
const copper = stageMat(0x3d6b55, { roughness: 0.48, metalness: 0.32 });
const copperDeep = stageMat(0x2a5544, { roughness: 0.42, metalness: 0.38 });
const copperBright = stageMat(0x4a7d64, { roughness: 0.4, metalness: 0.35 });
const canopy = stageMat(0x3a5a48, { roughness: 0.55, metalness: 0.15 });
const canopyDark = stageMat(0x2a4035, { roughness: 0.6 });
const glass = stageMat(0x1c2228, { roughness: 0.35, metalness: 0.2 });
const sash = stageMat(0xd8d0c4, { roughness: 0.75 });
const sill = stageMat(0x9a9084, { roughness: 0.85 });

function box(
  w: number,
  h: number,
  d: number,
  material: StageMaterial,
  x = 0,
  y = 0,
  z = 0,
) {
  const m = withOutline(new Mesh(new BoxGeometry(w, h, d), material), 1.014);
  m.position.set(x, y, z);
  return m;
}

function cylinder(
  rTop: number,
  rBot: number,
  h: number,
  segments: number,
  material: StageMaterial,
  x = 0,
  y = 0,
  z = 0,
) {
  const m = withOutline(
    new Mesh(new CylinderGeometry(rTop, rBot, h, segments), material),
    1.016,
  );
  m.position.set(x, y, z);
  return m;
}

/** Horizontal limestone band — breaks Minecraft slab faces. */
function stringCourse(
  parent: Group,
  w: number,
  d: number,
  x: number,
  y: number,
  z: number,
  thick = 0.45,
) {
  parent.add(box(w + 0.4, thick, d + 0.35, stoneLight, x, y, z));
  parent.add(box(w + 0.15, thick * 0.55, d + 0.55, stoneDark, x, y - thick * 0.35, z));
}

/** Roof cornice with eave overhang. */
function cornice(
  parent: Group,
  w: number,
  d: number,
  x: number,
  y: number,
  z: number,
) {
  parent.add(box(w + 1.2, 0.7, d + 1.2, stoneDark, x, y, z));
  parent.add(box(w + 1.6, 0.35, d + 1.6, stoneMid, x, y + 0.45, z));
  parent.add(box(w + 0.8, 0.25, d + 0.8, copperDeep, x, y + 0.7, z));
}

/**
 * Framed window unit: reveal → sash → glass → mullion → sill → lintel.
 * This is the finesse that flat dark stamps lack.
 */
function windowUnit(
  parent: Group,
  x: number,
  y: number,
  z: number,
  face: "s" | "n" | "e" | "w",
  w = 1.15,
  h = 1.85,
) {
  const depth = 0.55;
  const nz = face === "s" ? 1 : face === "n" ? -1 : 0;
  const nx = face === "e" ? 1 : face === "w" ? -1 : 0;
  const zFace = z + nz * 0.02;
  const xFace = x + nx * 0.02;

  // Reveal / frame
  if (nz !== 0) {
    parent.add(box(w + 0.35, h + 0.4, 0.28, stoneMid, xFace, y, zFace + nz * 0.12));
    parent.add(box(w + 0.12, h + 0.12, 0.12, sash, xFace, y, zFace + nz * 0.28));
    parent.add(box(w, h, 0.08, glass, xFace, y, zFace + nz * 0.38));
    // Mullions
    parent.add(box(0.08, h * 0.92, 0.1, sash, xFace, y, zFace + nz * 0.42));
    parent.add(box(w * 0.92, 0.08, 0.1, sash, xFace, y, zFace + nz * 0.42));
    // Sill + lintel
    parent.add(box(w + 0.5, 0.18, 0.4, sill, xFace, y - h * 0.55, zFace + nz * 0.2));
    parent.add(box(w + 0.4, 0.22, 0.32, stoneLight, xFace, y + h * 0.55, zFace + nz * 0.18));
  } else {
    parent.add(box(0.28, h + 0.4, w + 0.35, stoneMid, xFace + nx * 0.12, y, z));
    parent.add(box(0.12, h + 0.12, w + 0.12, sash, xFace + nx * 0.28, y, z));
    parent.add(box(0.08, h, w, glass, xFace + nx * 0.38, y, z));
    parent.add(box(0.1, h * 0.92, 0.08, sash, xFace + nx * 0.42, y, z));
    parent.add(box(0.1, 0.08, w * 0.92, sash, xFace + nx * 0.42, y, z));
    parent.add(box(0.4, 0.18, w + 0.5, sill, xFace + nx * 0.2, y - h * 0.55, z));
    parent.add(box(0.32, 0.22, w + 0.4, stoneLight, xFace + nx * 0.18, y + h * 0.55, z));
  }
  void depth;
}

function windowGrid(
  parent: Group,
  cols: number,
  rows: number,
  y0: number,
  face: "s" | "n" | "e" | "w",
  width: number,
  startX: number,
  zFace: number,
  rowH = 4.6,
  winW = 1.1,
  winH = 1.75,
) {
  const gap = width / (cols + 1);
  for (let r = 0; r < rows; r++) {
    for (let c = 1; c <= cols; c++) {
      const wx = startX + gap * c;
      const wy = y0 + r * rowH;
      windowUnit(parent, wx, wy, zFace, face, winW, winH);
    }
  }
}

/** Crossed-gable copper pavilion with eaves + ridge. */
function pavilionRoof(
  w: number,
  d: number,
  h: number,
  x: number,
  y: number,
  z: number,
  withCone = false,
) {
  const g = new Group();
  g.name = "pavilion_roof";
  // Eaves plate
  g.add(box(w * 1.12, 0.35, d * 1.12, stoneDark, x, y + 0.1, z));
  // Crossed copper volumes
  g.add(box(w * 1.08, h * 0.42, d * 0.36, copper, x, y + h * 0.22, z));
  g.add(box(w * 0.36, h * 0.42, d * 1.08, copper, x, y + h * 0.22, z));
  g.add(box(w * 0.95, h * 0.48, d * 0.24, copperDeep, x, y + h * 0.52, z));
  g.add(box(w * 0.24, h * 0.48, d * 0.95, copperDeep, x, y + h * 0.52, z));
  // Ridge caps
  g.add(box(w * 0.9, 0.2, 0.35, copperBright, x, y + h * 0.78, z));
  g.add(box(0.35, 0.2, d * 0.9, copperBright, x, y + h * 0.78, z));
  if (withCone) {
    const tip = withOutline(
      new Mesh(new ConeGeometry(Math.min(w, d) * 0.2, h * 0.4, 10), copperDeep),
      1.02,
    );
    tip.position.set(x, y + h * 0.95, z);
    g.add(tip);
  }
  return g;
}

/** Accent turret with corbel ring + flared copper cap. */
function turret(radius: number, shaftH: number, x: number, z: number) {
  const g = new Group();
  g.name = "turret";
  g.add(cylinder(radius, radius * 1.06, shaftH, 12, stone, 0, shaftH / 2, 0));
  // Corbel / string rings
  g.add(cylinder(radius * 1.14, radius * 1.1, 0.45, 12, stoneDark, 0, shaftH * 0.35, 0));
  g.add(cylinder(radius * 1.16, radius * 1.12, 0.5, 12, stoneLight, 0, shaftH * 0.72, 0));
  g.add(cylinder(radius * 1.2, radius * 1.14, 0.55, 12, stoneDark, 0, shaftH - 0.15, 0));
  // Narrow windows on shaft
  for (const ang of [0, Math.PI * 0.5, Math.PI, Math.PI * 1.5]) {
    const wx = Math.sin(ang) * radius * 0.92;
    const wz = Math.cos(ang) * radius * 0.92;
    g.add(box(0.35, 1.4, 0.2, glass, wx, shaftH * 0.45, wz));
  }
  const roofH = shaftH * 0.48;
  const roof = withOutline(
    new Mesh(new ConeGeometry(radius * 1.28, roofH, 12), copperDeep),
    1.02,
  );
  roof.position.set(0, shaftH + roofH * 0.42, 0);
  g.add(roof);
  // Finial nub
  g.add(cylinder(0.12, 0.18, 1.1, 6, copperBright, 0, shaftH + roofH * 0.85, 0));
  g.position.set(x, 0, z);
  return g;
}

function dormer(x: number, y: number, z: number, faceSign: number) {
  const g = new Group();
  g.name = "dormer";
  g.add(box(1.7, 1.9, 1.5, stoneMid, x, y, z));
  g.add(box(1.95, 0.25, 1.7, stoneDark, x, y + 1.05, z));
  g.add(box(1.85, 1.0, 1.55, copperDeep, x, y + 1.55, z));
  g.add(box(0.55, 0.2, 1.4, copperBright, x, y + 2.1, z));
  g.add(box(0.7, 1.15, 0.12, glass, x, y + 0.15, z + faceSign * 0.82));
  g.add(box(0.08, 1.05, 0.1, sash, x, y + 0.15, z + faceSign * 0.88));
  g.add(box(0.95, 0.14, 0.35, sill, x, y - 0.85, z + faceSign * 0.55));
  return g;
}

function chimney(x: number, y: number, z: number) {
  const g = new Group();
  g.name = "chimney";
  g.add(box(1.45, 3.2, 1.15, stoneDark, x, y, z));
  g.add(box(1.7, 0.35, 1.4, stoneMid, x, y + 1.2, z));
  g.add(box(1.85, 0.45, 1.5, stoneLight, x, y + 1.85, z));
  g.add(box(0.35, 0.7, 0.35, stoneDark, x - 0.35, y + 2.3, z));
  g.add(box(0.35, 0.7, 0.35, stoneDark, x + 0.35, y + 2.3, z));
  return g;
}

/** Quoin blocks at corner — vertical punctuation. */
function quoins(
  parent: Group,
  x: number,
  z: number,
  h: number,
  y0 = 2,
) {
  const n = Math.floor(h / 2.2);
  for (let i = 0; i < n; i++) {
    const odd = i % 2 === 0;
    parent.add(
      box(odd ? 1.4 : 1.05, 1.0, odd ? 1.05 : 1.4, stoneLight, x, y0 + i * 2.2, z),
    );
  }
}

export function createChateauFrontenacModel(): Group {
  const root = new Group();
  root.name = "chateau-frontenac-hero";
  root.userData = {
    kind: "hero-prop",
    id: "chateau-frontenac",
    source: "stylized procedural hero · finesse pass",
    sockets: {
      interact: { x: 0, y: 16, z: 14 },
      hunt: { x: 0, y: 8, z: 16 },
      camera: { x: 58, y: 42, z: 72 },
    },
  };

  const pivot = new Group();
  pivot.name = "pivot_root";
  root.add(pivot);

  // —— Keep ——
  const keep = new Group();
  keep.name = "keep";
  const kx = -5;
  const kz = -2;
  // Rusticated base
  keep.add(box(21, 5.5, 21, stoneDark, kx, 2.75, kz));
  keep.add(box(20.4, 48, 20.4, stone, kx, 29, kz));
  stringCourse(keep, 20.6, 20.6, kx, 8, kz);
  stringCourse(keep, 20.6, 20.6, kx, 16, kz);
  stringCourse(keep, 20.6, 20.6, kx, 24, kz);
  stringCourse(keep, 20.6, 20.6, kx, 36, kz);
  stringCourse(keep, 20.6, 20.6, kx, 46, kz);
  cornice(keep, 20, 20, kx, 53.2, kz);
  keep.add(box(17.5, 6.5, 17.5, stoneMid, kx, 57.5, kz));
  stringCourse(keep, 17.5, 17.5, kx, 60.5, kz, 0.35);
  keep.add(pavilionRoof(16, 16, 11, kx, 61.5, kz, true));
  // Lantern
  keep.add(cylinder(2.5, 2.7, 5.2, 12, stone, kx, 74, kz));
  keep.add(cylinder(2.9, 2.7, 0.45, 12, stoneDark, kx, 71.6, kz));
  keep.add(box(5.6, 1.0, 5.6, copperDeep, kx, 77.2, kz));
  for (const [dx, dz] of [
    [-2.5, -2.5],
    [2.5, -2.5],
    [-2.5, 2.5],
    [2.5, 2.5],
  ] as const) {
    keep.add(box(0.32, 2.4, 0.32, copperBright, kx + dx, 75.8, kz + dz));
  }
  windowGrid(keep, 4, 6, 7, "s", 15, kx - 7.5, kz + 10.25, 6.2);
  windowGrid(keep, 3, 5, 9, "n", 12, kx - 6, kz - 10.25, 6.2);
  {
    const cols = 3;
    const rows = 5;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        windowUnit(keep, kx + 10.25, 8 + r * 6.2, kz - 5 + c * 5, "e", 1.0, 1.6);
        windowUnit(keep, kx - 10.25, 8 + r * 6.2, kz - 5 + c * 5, "w", 1.0, 1.6);
      }
    }
  }
  keep.add(chimney(kx - 5.5, 66, kz - 4));
  keep.add(chimney(kx + 5.5, 66, kz - 4));
  keep.add(turret(2.5, 26, kx + 9.5, kz - 8));
  quoins(keep, kx + 9.8, kz + 9.8, 48);
  quoins(keep, kx - 9.8, kz + 9.8, 48);
  pivot.add(keep);

  // Infill east of keep
  const infill = new Group();
  infill.add(box(9, 22, 13, stoneMid, 12, 11, -8));
  stringCourse(infill, 9, 13, 12, 8, -8);
  stringCourse(infill, 9, 13, 12, 16, -8);
  cornice(infill, 8.5, 12.5, 12, 22.2, -8);
  infill.add(pavilionRoof(7.5, 11, 7, 12, 23, -8, false));
  windowGrid(infill, 2, 3, 6, "s", 7, 8.5, -8 + 6.6, 5);
  pivot.add(infill);

  // —— EAST wing ——
  const wingE = new Group();
  wingE.name = "wing_east";
  wingE.add(box(72, 5, 20.5, stoneDark, 40, 2.5, -3));
  wingE.add(box(72, 26, 20, stone, 40, 16, -3));
  wingE.add(box(72, 5, 22.5, stoneDark, 36, 2.5, -18));
  wingE.add(box(72, 24, 22, stone, 36, 15, -18));
  for (const y of [7, 12, 17, 22, 27]) {
    stringCourse(wingE, 72.2, 20.2, 40, y, -3);
    stringCourse(wingE, 72.2, 22.2, 36, y, -18);
  }
  cornice(wingE, 72, 20, 40, 29.5, -3);
  cornice(wingE, 72, 22, 36, 27.5, -18);
  const eastPavilions = [
    [12, 31, -3],
    [28, 32, -3],
    [44, 31.5, -3],
    [58, 32.5, -3],
    [20, 30, -18],
    [48, 30.5, -18],
  ] as const;
  for (const [px, py, pz] of eastPavilions) {
    wingE.add(pavilionRoof(11, 14, 9.5, px, py, pz, false));
  }
  windowGrid(wingE, 12, 4, 6.5, "s", 66, 8, 7.15, 5.2);
  windowGrid(wingE, 11, 3, 7, "n", 64, 6, -29.15, 5.5, 0.95, 1.5);
  for (let i = 0; i < 8; i++) {
    wingE.add(dormer(12 + i * 7.5, 30.4, 6.8, 1));
  }
  wingE.add(chimney(30, 37, -8));
  wingE.add(chimney(48, 38, -8));
  wingE.add(chimney(58, 36, -20));
  wingE.add(turret(2.7, 26, 72, -6));
  quoins(wingE, 75.5, 6.5, 28, 3);
  quoins(wingE, 4.5, 6.5, 28, 3);
  pivot.add(wingE);

  // —— WEST wing ——
  const wingW = new Group();
  wingW.name = "wing_west";
  wingW.add(box(36, 4.5, 16.5, stoneDark, -22, 2.25, -1));
  wingW.add(box(36, 19, 16, stone, -22, 12.5, -1));
  for (const y of [6, 11, 16, 20]) {
    stringCourse(wingW, 36.2, 16.2, -22, y, -1);
  }
  cornice(wingW, 36, 16, -22, 22.3, -1);
  for (const [px, py, pz] of [
    [-10, 23.5, -1],
    [-22, 24.2, -1],
    [-34, 23.2, -1],
  ] as const) {
    wingW.add(pavilionRoof(9, 12, 8, px, py, pz, false));
  }
  windowGrid(wingW, 7, 3, 6, "s", 32, -38, 7.15, 4.8);
  for (let i = 0; i < 5; i++) {
    wingW.add(dormer(-34 + i * 6.5, 22.8, 6.2, 1));
  }
  wingW.add(chimney(-24, 29, -5));
  wingW.add(turret(2.35, 20, -38, 2));
  quoins(wingW, -39.5, 6.5, 20, 2);
  pivot.add(wingW);

  // —— Place d’Armes façade ——
  const fore = new Group();
  fore.name = "forecourt";
  fore.add(box(10, 15, 6.5, stone, -16, 7.5, 8));
  fore.add(box(10, 15, 6.5, stone, 12, 7.5, 8));
  fore.add(box(26, 17, 8.5, stone, 0, 8.5, 8));
  stringCourse(fore, 26, 8.5, 0, 6, 8);
  stringCourse(fore, 26, 8.5, 0, 12, 8);
  cornice(fore, 26, 8.5, 0, 17.3, 8);
  // Central entrance pavilion
  fore.add(box(13, 15, 6.5, stoneMid, 0, 9.5, 10.5));
  stringCourse(fore, 13, 6.5, 0, 7, 10.5);
  cornice(fore, 12.5, 6, 0, 17.2, 10.5);
  fore.add(pavilionRoof(11.5, 7.5, 7.5, 0, 18.5, 10.5, false));
  // Porte-cochère
  fore.add(box(12.5, 1.2, 5.5, canopy, 0, 7.8, 11.5));
  fore.add(box(13, 0.35, 6, canopyDark, 0, 8.5, 11.5));
  for (const [cx, cz] of [
    [-5.2, 9.8],
    [5.2, 9.8],
    [-5.2, 12.2],
    [5.2, 12.2],
  ] as const) {
    fore.add(cylinder(0.48, 0.55, 7, 10, canopyDark, cx, 3.5, cz));
    fore.add(cylinder(0.62, 0.58, 0.35, 10, stoneLight, cx, 7.1, cz));
  }
  // Arch jambs + keystone
  fore.add(box(5.2, 6.8, 1.3, stoneDark, -4.2, 4.2, 13));
  fore.add(box(5.2, 6.8, 1.3, stoneDark, 4.2, 4.2, 13));
  fore.add(box(12.5, 1.5, 1.5, stoneLight, 0, 8.0, 13));
  fore.add(box(1.2, 1.0, 0.5, stoneLight, 0, 8.8, 13.4));
  fore.add(box(4.2, 5.2, 0.35, glass, 0, 4.0, 13.5));
  windowGrid(fore, 2, 3, 6, "s", 8, -20, 11.3, 4.2);
  windowGrid(fore, 2, 3, 6, "s", 8, 8, 11.3, 4.2);
  for (let i = 0; i < 3; i++) {
    fore.add(dormer(-8 + i * 8, 18.2, 11.2, 1));
  }
  fore.add(turret(2.25, 18, -14, 10));
  quoins(fore, -20.5, 10.5, 15, 2);
  quoins(fore, 16.5, 10.5, 15, 2);
  pivot.add(fore);

  // Plinth under masonry only
  pivot.add(box(112, 2.8, 44, stoneDark, -4, 1.4, -10));
  pivot.add(box(108, 0.9, 40, stoneMid, -4, 3.1, -10));
  // Subtle terrace step (not plaza slab)
  pivot.add(box(40, 0.5, 8, stoneMid, 0, 0.35, 6));

  const collider = box(118, 84, 76, stone, -4, 38, -8);
  collider.visible = false;
  collider.name = "collider";
  collider.userData.collider = true;
  pivot.add(collider);

  const interact = new Group();
  interact.name = "socket_interact";
  interact.position.set(0, 8, 16);
  pivot.add(interact);

  root.userData.tick = (_dt: number) => {};
  return root;
}
